import { useEffect } from "react";

/**
 * Strukturierte Daten für SEO/Google Rich Snippets.
 *
 * Wird als EIN einzelnes <script type="application/ld+json"> im <head> eingehängt
 * (mit @graph) — keine doppelten/widersprüchlichen Schemata.
 *
 * Setup ist idempotent: bei Re-Mount wird der vorhandene Tag ersetzt, nicht dupliziert.
 *
 * Quelle für dynamische Felder: settings (phone/email/whatsapp aus DB).
 * Adresse + Öffnungszeiten werden bewusst NICHT eingetragen, solange diese nicht
 * in der DB gepflegt sind (keine erfundenen Daten).
 */

const SITE_URL = "https://anfrage.bekajbau.de/";
const ELEMENT_ID = "bekaj-jsonld-schema";

const AREAS = [
  "Großkrotzenburg", "Hanau", "Aschaffenburg", "Hainburg",
  "Seligenstadt", "Kahl am Main", "Alzenau", "Maintal", "Rodgau",
];

const SERVICES = [
  "Innenausbau", "Trockenbau", "Renovierung", "Sanierung",
  "Fassadenarbeiten", "Bodenbeschichtungen", "Malerarbeiten", "Fliesenarbeiten",
];

const FAQS = [
  {
    q: "In welchen Regionen ist BEKAJ BAU tätig?",
    a: "BEKAJ BAU ist in Großkrotzenburg ansässig und arbeitet im gesamten Rhein-Main-Gebiet — schwerpunktmäßig in Hanau, Aschaffenburg, Hainburg, Seligenstadt, Kahl am Main, Alzenau, Maintal und Rodgau.",
  },
  {
    q: "Welche Leistungen bietet BEKAJ BAU an?",
    a: "Innenausbau, Renovierungen, Sanierungen, Fassadenarbeiten, Bodenbeschichtungen, Trockenbau, Maler- und Fliesenarbeiten — von der Beratung bis zur Abnahme aus einer Hand.",
  },
  {
    q: "Ist die Erstberatung kostenlos?",
    a: "Ja. Die Erstberatung und die Einschätzung Ihres Bauprojekts sind kostenlos und unverbindlich.",
  },
  {
    q: "Wie schnell erhalte ich eine Rückmeldung?",
    a: "Wir melden uns in der Regel innerhalb von 24 Stunden bei Ihnen — werktags meist deutlich schneller.",
  },
  {
    q: "Kann ich mein Projekt direkt per WhatsApp anfragen?",
    a: "Ja. Über den WhatsApp-Button auf der Website können Sie uns Fotos, Maße und eine Beschreibung Ihres Projekts direkt zusenden.",
  },
];

/**
 * Normalisiert E.164-ähnliche Telefonnummer auf +E164.
 * settings.phone wird ohne "+" gespeichert (z.B. "4917620283482") → "+4917620283482".
 */
const toE164 = (raw) => {
  if (!raw) return null;
  const digits = String(raw).replace(/\D+/g, "");
  if (!digits) return null;
  return "+" + digits;
};

const buildGraph = (settings) => {
  const telephone = toE164(settings?.phone);
  const email = settings?.email || undefined;

  const businessBase = {
    "@id": SITE_URL + "#business",
    name: "BEKAJ BAU",
    url: SITE_URL,
    description:
      "BEKAJ BAU aus Großkrotzenburg bietet Innenausbau, Renovierungen, Sanierungen, Fassadenarbeiten und Bodenbeschichtungen im Raum Hanau, Aschaffenburg und Umgebung.",
    areaServed: AREAS.map((name) => ({ "@type": "City", name })),
    knowsLanguage: ["de"],
    ...(telephone ? { telephone } : {}),
    ...(email ? { email } : {}),
  };

  const business = {
    "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
    ...businessBase,
  };

  // Eigenständige Service-Nodes
  const serviceNodes = SERVICES.map((s) => ({
    "@type": "Service",
    "@id": SITE_URL + "#service-" + s.toLowerCase(),
    name: s,
    provider: { "@id": SITE_URL + "#business" },
    areaServed: AREAS.map((name) => ({ "@type": "City", name })),
    serviceType: s,
  }));

  // FAQPage
  const faqPage = {
    "@type": "FAQPage",
    "@id": SITE_URL + "#faq",
    mainEntity: FAQS.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  return {
    "@context": "https://schema.org",
    "@graph": [business, ...serviceNodes, faqPage],
  };
};

const JsonLd = ({ settings }) => {
  useEffect(() => {
    const graph = buildGraph(settings);
    const json = JSON.stringify(graph);
    let el = document.getElementById(ELEMENT_ID);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = ELEMENT_ID;
      document.head.appendChild(el);
    }
    el.text = json;
    // Cleanup: NICHT entfernen — der Tag soll auch beim Wechsel der Subseite verfügbar bleiben.
    // Beim erneuten Mount wird er nur aktualisiert (siehe oben).
  }, [settings]);

  return null;
};

export default JsonLd;
